import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import { CheckboxGroup, motion } from '@hover/blueprint';
import { DarkMode, LightMode } from '@hover/blueprint/chakra';
import { ensureError, mapValues } from '@hover/utilities';
import React, { useEffect, useState } from 'react';

import { pickForegroundColor } from 'src/modules/homeownerApp/helpers/color';
import type { ImagePixelData } from 'src/modules/homeownerApp/helpers/images';
import {
  calculateAverageImageColor,
  getImageData,
} from 'src/modules/homeownerApp/helpers/images';
import { logError } from 'src/shared/telemetry/logger';

import type { SegmentHandleProps } from './SegmentHandle';
import { SegmentHandle } from './SegmentHandle';
import type { SegmentationHighlightsProps } from './SegmentationHighlights';
import { SegmentationHighlights } from './SegmentationHighlights';
import { useSegmentMaterials } from './hooks';
import type {
  HandlePlacementProps,
  PlacementWithColorMode,
  SegmentationProps,
} from './types';

export interface SegmentationOverlayProps
  extends SegmentationProps,
    HandlePlacementProps,
    MotionPropsFor<IntrinsicSystemProps<any>>,
    Pick<SegmentHandleProps, 'handleFunctionsRef'> {
  defaultSelectedHandles?: string[];
  handlesProps?: MotionPropsFor<IntrinsicSystemProps<'div'>>;
  highlightsProps?: MotionPropsFor<Partial<SegmentationHighlightsProps>>;
  /**
   * Optional image to sample colors from in order to determine whether to
   * display segment handles in light or dark mode
   */
  imageSource?: string;
  /**
   * Selectively apply blur animation on the segment handles which is necessary
   * to avoid some browser artifacting when fading the handles in and out
   */
  isVisible?: boolean;
  isTooltipOpen?: boolean;
  onSelectedHandlesChange: (selected: string[]) => void;
  selectedHandles?: string[];
  tooltip?: string;
}

type TooltipPlacementId = string | null;
type ImageDataResult = ImagePixelData | { error: Error } | undefined;

const MotionSegmentationHighlights = motion(SegmentationHighlights);

export const SegmentationOverlay = ({
  data,
  debug = false,
  defaultSelectedHandles,
  handleFunctionsRef,
  handlePlacementMode = 'material',
  handlesProps = {},
  highlightsProps = {},
  imageSource,
  isVisible = true,
  isTooltipOpen = false,
  onSelectedHandlesChange,
  selectedHandles,
  selectedSegments,
  tooltip,
  weighted,
  ...props
}: SegmentationOverlayProps) => {
  const [imageData, setImageData] = useState<ImageDataResult>();

  const [tooltipHandleId, setTooltipHandleId] =
    useState<TooltipPlacementId>(null);
  const [handlePlacements, setHandlePlacements] = useState<
    Record<string, PlacementWithColorMode>
  >({});

  const { width, height } = data.dimensions;

  const materials = useSegmentMaterials(data.segments);
  const handles =
    handlePlacementMode === 'instance' ? data.segments : materials;

  useEffect(() => {
    const keys =
      handlePlacementMode === 'instance'
        ? data.segments.map(segment => segment.id)
        : materials;

    setHandlePlacements(previous =>
      Object.fromEntries(
        Object.entries(previous).filter(([key]) => keys.includes(key)),
      ),
    );
  }, [data.segments, handlePlacementMode, materials]);

  useEffect(() => {
    void (async () => {
      if (!imageSource) return;

      try {
        setImageData(await getImageData(imageSource));
      } catch (caught) {
        const error = ensureError(caught);

        error.message = `SegmentationOverlay: failed to load image data (${error.message})`;

        setImageData({ error });
        logError(error);
      }
    })();
  }, [imageSource]);

  useEffect(() => {
    if (!imageData || 'error' in imageData) return;

    setHandlePlacements(previous =>
      mapValues(previous, placement => {
        const { x, y } = placement;

        const color = calculateAverageImageColor(imageData, {
          x,
          y,
          threshold: 20,
        });

        if (!color) return placement;

        const { mode: colorMode } = pickForegroundColor(
          color,
          '#ffffff',
          '#000000',
        );

        return {
          ...placement,
          colorMode,
        };
      }),
    );
  }, [imageData]);

  useEffect(() => {
    setTooltipHandleId(
      Object.entries(handlePlacements).reduce(
        (previousId, [currentId, { x, y }]) => {
          if (!previousId) return currentId;

          const previous = handlePlacements[previousId];

          return x + y < previous.x + previous.y ? currentId : previousId;
        },
        null as TooltipPlacementId,
      ),
    );
  }, [handlePlacements]);

  return (
    <>
      <MotionSegmentationHighlights
        data={data}
        debug={debug}
        handlePlacementMode={handlePlacementMode}
        left={0}
        onPlaceInstanceHandle={(segment, placement) => {
          if (handlePlacementMode !== 'instance') return;

          setHandlePlacements(previous => ({
            ...previous,
            [segment.id]: placement,
          }));
        }}
        onPlaceMaterialHandle={(material, placement) => {
          if (handlePlacementMode !== 'material') return;

          setHandlePlacements(previous => ({
            ...previous,
            [material]: placement,
          }));
        }}
        pointerEvents="none"
        selectedSegments={selectedSegments}
        weighted={weighted}
        {...props}
        {...highlightsProps}
      />
      <motion.div
        bottom={0}
        left={0}
        opacity={debug ? 0.5 : undefined}
        pointerEvents="none"
        position="absolute"
        right={0}
        top={0}
        {...props}
        {...handlesProps}
      >
        <CheckboxGroup
          defaultValue={defaultSelectedHandles}
          onChange={onSelectedHandlesChange}
          value={selectedHandles}
        >
          {(!imageSource || imageData) &&
            handles.map((handle, index) => {
              const isMaterial = typeof handle === 'string';
              const id = isMaterial ? handle : handle.id;

              const { x, y, colorMode } = handlePlacements[id] ?? {
                x: 0,
                y: 0,
              };

              const Mode = colorMode === 'dark' ? DarkMode : LightMode;
              const isTooltipHandle = tooltipHandleId === id;

              const left = x / width;
              const top = y / height;

              return (
                <Mode key={id}>
                  <SegmentHandle
                    handleFunctionsRef={
                      isTooltipHandle ? handleFunctionsRef : undefined
                    }
                    isTooltipOpen={
                      !!tooltip && isTooltipOpen && isTooltipHandle
                    }
                    isVisible={isVisible}
                    label={isMaterial ? handle : `${handle.material} ${index}`}
                    left={`${left * 100}%`}
                    pointerEvents="auto"
                    position="absolute"
                    tooltip={tooltip}
                    tooltipDirection={{
                      x: left < 0.5 ? 'end' : 'start',
                      y: top < 0.5 ? 'end' : 'start',
                    }}
                    top={`${top * 100}%`}
                    value={id}
                    zIndex={isTooltipHandle ? 2 : 1}
                  />
                </Mode>
              );
            })}
        </CheckboxGroup>
      </motion.div>
    </>
  );
};
